import debounce from 'lodash.debounce';

import cInput from '#themes/default/default/components/input/input.js';
import cSvg from '#themes/default/default/components/svg/svg.js';
import cTooltip from '#themes/default/default/components/tooltip/tooltip.js';

import template from './quantity.html' with { type: 'html' };

export default {
	name: 'CQuantity',
	components: {
		cInput,
		cSvg,
		cTooltip
	},
	inheritAttrs: false,
	props: {
		decrement: {
			type: Number,
			default: 1
		},
		increment: {
			type: Number,
			default: 1
		},
		isDisabled: {
			type: Boolean,
			default: false
		},
		isLoading: {
			type: Boolean,
			default: false
		},
		label: {
			type: String,
			default: ''
		},
		product: {
			type: Object,
			default: () => {}
		},
		theme: {
			type: Object,
			default: () => {}
		},
		utils: {
			type: Object,
			default: () => null
		}
	},
	data() {
		return {
			quantity: this.product.quantity,
			debouncedQuantityChange: null,
			currentTimeout: 0,
			buttonTimeoutDuration: 500
		};
	},
	computed: {
		maxLimit() {
			return this.product.maxQuantityLimit;
		},
		minLimit() {
			return this.product.minQuantityLimit;
		},
		isUsage() {
			return this.product.quantityAs === 'usage';
		},
		isTooltipVisible() {
			return this.$refs.quantityTooltip.show;
		}
	},
	watch: {
		async quantity(newQuantity, previousQuantity) {
			// Cancel change event if the quantity changes within the timeout time period
			if (typeof this.debouncedQuantityChange === 'function') {
				this.debouncedQuantityChange.cancel();
			}
			if (newQuantity !== previousQuantity) {
				this.debouncedQuantityChange = debounce(async () => {
					await this.changeQuantity(newQuantity);
				}, this.currentTimeout);
				// Change quantity after the timeout
				await this.debouncedQuantityChange();
			}
		}
	},
	methods: {
		getElementDisabledState(condition = false) {
			return condition || this.isLoading || this.isDisabled ? 'disabled' : null;
		},
		async changeQuantity(quantity) {
			await this.$store.dispatch('changeQuantity', {
				product: this.product.id,
				quantity
			});
			await this.updateUrlQuery();
		},
		setQuantity(event) {
			const previousQuantity = this.quantity;
			const newQuantity = Number(event.value);
			const quantityReference = this.$refs.quantity;
			const tooltipButtonReference = this.$refs.quantityTooltipButton;
			// Set timeout between cart refresh to zero
			this.currentTimeout = 0;
			if (newQuantity >= this.minLimit && newQuantity <= this.maxLimit) {
				this.quantity = newQuantity;
				// Force reactivity of the value attribute
				quantityReference.$forceUpdate();
				// Hide tooltip
				if (this.isTooltipVisible) {
					tooltipButtonReference.click();
				}
				return false;
			}
			// Show tooltip if entered quantity isn't within the limit and tooltip is not displayed yet
			if (newQuantity > this.maxLimit && !this.isTooltipVisible) {
				tooltipButtonReference.click();
			}
			this.quantity = previousQuantity;
			// Force reactivity of the value attribute
			quantityReference.$forceUpdate();
			return false;

		},
		increaseQuantity() {
			const finalQuantity = this.quantity + this.increment;
			// Set delay between quantity change by increase button click
			this.currentTimeout = this.buttonTimeoutDuration;
			if (finalQuantity <= this.maxLimit) {
				this.quantity += this.increment;
			}
		},
		decreaseQuantity() {
			// Set delay between quantity change by decrease button click
			this.currentTimeout = this.buttonTimeoutDuration;
			// Decrease quantity
			this.quantity -= this.decrement;
		}
	},
	template
};
